import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalPosting } from '../../context/PortalPostingContext';
import { useAuth } from '../../context/AuthContext';

/**
 * The bar pinned to the bottom while your own Portal goes up.
 * Handoff: handoff-composer/README.md "Toast".
 *
 * It exists because the sender is the only person who knows the Portal exists yet — everyone
 * else is handed nothing until it is encoded — so this is the only place the wait is visible.
 */
const COPY = {
  uploading: {
    title: (pct) => `Posting your Portal · ${pct}%`,
    body: "Only you can see it until it's up. Keep using the app.",
    fill: '#FFFFFF',
  },
  finishing: {
    title: () => 'Finishing your Portal…',
    body: "Only you can see it until it's up. Keep using the app.",
    fill: '#FFFFFF',
  },
  up: {
    title: () => 'Your Portal is up',
    body: 'Your friends can see it now. 4h left.',
    fill: '#FF00AA',
    action: 'View it',
  },
  failed: {
    title: () => "Didn't post.",
    body: 'Your copy is still on this phone. Retry picks up where it stopped.',
    fill: '#EF4444',
    action: 'Retry',
    danger: true,
  },
};

export default function PortalPostingToast() {
  const navigate = useNavigate();
  const { posting, retry, dismiss } = usePortalPosting();
  const { user } = useAuth();
  if (!posting) return null;

  const spec = COPY[posting.status];
  if (!spec) return null;
  const pct = Math.round(posting.progress || 0);

  const onAction = () => {
    if (posting.status === 'failed') { retry(); return; }
    dismiss();
    // Straight into your own Portal. It used to go to /featured, which did nothing at all when
    // you were already on it.
    navigate(user?.id ? `/featured?reel=${user.id}` : '/featured');
  };

  return (
    <div className="fixed inset-x-0 bottom-0 safe-bottom-inset z-[85]" role="status" aria-live="polite">
      <div style={{ background: '#000000' }}>
        {/* The progress runs along the top edge of the bar itself. */}
        <div style={{ height: 3, background: '#3A3A3A' }} aria-hidden>
          <div
            style={{
              height: '100%',
              width: posting.status === 'uploading' ? `${pct}%` : '100%',
              background: spec.fill,
              transition: 'width 140ms linear',
            }}
          />
        </div>

        <div className="mx-auto flex max-w-content items-center gap-4 px-4 py-3 sm:px-8">
          <div className="min-w-0 flex-1">
            <p
              className="font-mono font-bold uppercase"
              style={{ fontSize: 11, letterSpacing: '0.10em', color: spec.danger ? '#EF4444' : '#FFFFFF' }}
            >
              {spec.title(pct)}
            </p>
            <p className="mt-1 text-white/70" style={{ fontSize: 12, lineHeight: 1.4 }}>{spec.body}</p>
          </div>

          {spec.action && (
            <button
              type="button"
              onClick={onAction}
              className="flex-shrink-0 font-mono font-bold uppercase text-white"
              style={{ border: '2px solid #FFFFFF', fontSize: 11, letterSpacing: '0.10em', padding: '8px 12px' }}
            >
              {spec.action}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
